import React, { useState } from 'react';
import { Box, Typography, Button, TextField, MenuItem, Grid } from '@mui/material';
import { useMeals } from '../hooks/useMeals';
import CloseIcon from '@mui/icons-material/Close';

const hungerScale = [
  { value: 1, label: '1 - Ravenous (starving, weak, dizzy)' },
  { value: 2, label: '2 - Uncomfortably Hungry (irritable, low energy)' },
  { value: 3, label: '3 - Very Hungry (stomach growling)' },
  { value: 4, label: '4 - A Little Hungry (thoughts turn to food)' },
  { value: 5, label: '5 - Neutral (neither hungry nor full)' },
  { value: 6, label: '6 - Satisfied + Light (slightly full)' },
  { value: 7, label: '7 - Full (slightly uncomfortable)' },
  { value: 8, label: '8 - Very Full (feeling stuffed)' },
  { value: 9, label: '9 - Very Uncomfortable (urge for stretchy pants)' },
  { value: 10, label: '10 - Painfully Full (extreme discomfort, feel sick)' },
];

interface HungerScaleEntryProps {
  onBack?: () => void;
}

const HungerScaleEntry: React.FC<HungerScaleEntryProps> = ({ onBack }) => {
  const [preMeal, setPreMeal] = useState('');
  const [postMeal, setPostMeal] = useState('');
  const [calories, setCalories] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { createMeal, loading, error } = useMeals();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const entry: { preMealScore: number; postMealScore: number; calories?: number } = {
      preMealScore: Number(preMeal),
      postMealScore: Number(postMeal),
    };
    if (calories !== '') {
      entry.calories = Number(calories);
    }

    const success = await createMeal(entry);
    if (success) {
      setSubmitted(true);
      setTimeout(() => {
        setPreMeal('');
        setPostMeal('');
        setCalories('');
        setSubmitted(false);
      }, 1500);
    }
  };

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', mt: 8, p: 3, bgcolor: '#fff', borderRadius: 3, boxShadow: 2, position: 'relative' }}>
      {/* Back Button */}
      {onBack && (
        <Button
          onClick={onBack}
          variant="text"
          size="small"
          sx={{ position: 'absolute', top: 8, left: 8, minWidth: 0, p: 1 }}
        >
          <CloseIcon sx={{ color: '#222', fontSize: 32 }} />
        </Button>
      )}
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, mt: 4, textAlign: 'center' }}>
        Meal Entry
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
        Enter your hunger scale score before and after your meal.
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              label="Pre-meal score"
              value={preMeal}
              onChange={e => setPreMeal(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            >
              {hungerScale.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Post-meal score"
              value={postMeal}
              onChange={e => setPostMeal(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            >
              {hungerScale.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Calories (optional)"
              type="number"
              value={calories}
              onChange={e => setCalories(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={submitted || loading}
              sx={{ py: 1.2, fontSize: '1.1rem', borderRadius: 8, backgroundColor: '#00A3FF', '&:hover': { backgroundColor: '#0091E6' } }}
            >
              {submitted ? 'Submitted!' : loading ? 'Saving...' : 'Submit'}
            </Button>
            {error && (
              <Typography color="error" sx={{ mt: 1, textAlign: 'center' }}>
                {error}
              </Typography>
            )}
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default HungerScaleEntry;
